// axios 封装：统一请求拦截 + token 注入 + 401 自动 refresh + 错误处理

import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { message, Modal } from 'ant-design-vue'
import { h } from 'vue'
import { requestReauth } from '@/utils/reauth'
import { translate } from '@/composables/useLocale'

// 生产环境留空时使用同源 /api，避免绕过 Nginx 访问另一套后端或触发 CORS。
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const API_PREFIX = import.meta.env.VITE_API_PREFIX || '/api/v1'
let TENANT_ID = import.meta.env.VITE_TENANT_ID || 'default'
let planRestrictionVisible = false
let agentSetupVisible = false

const currentLocale = () => localStorage.getItem('tikflow_locale') || 'zh-CN'
const t = (key: string, params: Record<string, string | number> = {}) => translate(currentLocale(), key, params)
// Video funds are independent from subscriptions; an HTTP 402 must not sell a plan.
const isVideoRequest = (url = '') => /^\/(ai-video|billing\/ai-video)(\/|$)/.test(url)
const localizedMessage = (value: unknown, fallbackKey: string) => {
  const text = String(value || '')
  return currentLocale() === 'en' && /\p{Script=Han}/u.test(text) ? t(fallbackKey) : (text || t(fallbackKey))
}

function showPlanRestriction(data: any, fallbackMessage: string) {
  if (planRestrictionVisible) return
  planRestrictionVisible = true
  const requestedPath = String(data?.upgrade_path || '/billing/plans')
  const upgradePath = requestedPath.startsWith('/billing/') ? requestedPath : '/billing/plans'
  const quotaDetail = data?.resource === 'environments' && Number.isFinite(Number(data?.limit))
    ? t('request.quota_detail', { used: data.used, limit: data.limit })
    : ''
  Modal.confirm({
    title: data?.resource === 'environments' ? t('request.environment_quota_full') : t('request.plan_unsupported'),
    content: `${localizedMessage(fallbackMessage, 'request.plan_restricted')}${quotaDetail}`,
    okText: t('request.view_plans'),
    cancelText: t('request.later'),
    onOk: () => {
      window.location.href = upgradePath
    },
    afterClose: () => {
      planRestrictionVisible = false
    },
  })
}

function showAgentSetup(data?: any) {
  if (data?.execution_mode === 'shared_free') {
    message.warning(t('shared.unavailable'))
    return
  }
  if (agentSetupVisible) return
  agentSetupVisible = true
  Modal.confirm({
    title: t('request.agent_required_title'),
    content: h('div', [
      h('p', t('request.agent_required_intro')),
      h('ol', { style: { paddingLeft: '20px', lineHeight: '1.8' } }, [
        h('li', t('request.agent_required_step_installed')),
        h('li', t('request.agent_required_step_new')),
        h('li', t('request.agent_required_step_retry')),
      ]),
    ]),
    okText: t('request.deploy_agent'),
    cancelText: t('request.later'),
    onOk: () => {
      window.location.href = '/nurture/console?deployAgent=1'
    },
    afterClose: () => {
      agentSetupVisible = false
    },
  })
}

// axios 实例
const request = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// 运行时切换租户（二步登录选定后，注入对应 X-Tenant-Id）
export function setTenantId(tid: string) {
  TENANT_ID = tid
}
export function getTenantId(): string {
  return readAuth('tenant_id') || TENANT_ID
}
export function clearTenantId() {
  TENANT_ID = import.meta.env.VITE_TENANT_ID || 'default'
}

// auth 存储：记住登录用 localStorage（持久），不记住用 sessionStorage（关浏览器失效）
// 读：localStorage 优先 → sessionStorage；写：写回来源 storage；删：双 storage 都清
function readAuth(key: string): string | null {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key)
}
function writeAuth(key: string, value: string) {
  if (localStorage.getItem(key) !== null) {
    localStorage.setItem(key, value)
  } else {
    sessionStorage.setItem(key, value)
  }
}
function removeAuth(key: string) {
  localStorage.removeItem(key)
  sessionStorage.removeItem(key)
}

// 请求拦截：注入 token + X-Tenant-Id
request.interceptors.request.use(
  (config) => {
    const token = readAuth('access_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // FormData 上传：去掉默认 Content-Type，让浏览器自动设置 multipart/form-data; boundary=...
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type']
    }

    // 页面刷新后模块变量 TENANT_ID 丢失为 default，优先读 storage 恢复租户头。
    if (config.headers) {
      config.headers['X-Tenant-Id'] = readAuth('tenant_id') || TENANT_ID
    }

    return config
  },
  (error) => Promise.reject(error),
)

// 是否正在刷新 token（防止并发重复刷新）
let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null

// token 刷新
async function tryRefreshToken(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  const refreshToken = readAuth('refresh_token')
  if (!refreshToken) {
    isRefreshing = false
    return null
  }

  refreshPromise = axios
    .post(`${API_BASE_URL}${API_PREFIX}/auth/refresh`, {
      refresh_token: refreshToken,
    }, {
      // 页面刷新后模块变量 TENANT_ID 丢失，优先读 storage 恢复租户头
      headers: { 'X-Tenant-Id': readAuth('tenant_id') || TENANT_ID },
    })
    .then((res) => {
      const data = res.data?.data
      if (data?.access_token) {
        writeAuth('access_token', data.access_token)
        if (data.refresh_token) writeAuth('refresh_token', data.refresh_token)
        return data.access_token as string
      }
      return null
    })
    .catch(() => null)
    .finally(() => {
      isRefreshing = false
      refreshPromise = null
    })

  return refreshPromise
}

// 清除 token 并跳转登录
function clearAuthAndRedirect() {
  removeAuth('access_token')
  removeAuth('refresh_token')
  removeAuth('user_info')
  window.location.href = '/login'
}

// 响应拦截：统一处理 code + 401 自动 refresh
request.interceptors.response.use(
  (response: AxiosResponse) => {
    const data = response.data

    // 非 API 响应（如下载文件）
    if (response.config.responseType === 'blob') {
      return response
    }

    // 业务成功
    if (data.code === 0) {
      return data.data
    }

    // 业务错误
    if ((data.code === 4021 || data.code === 4022) && !isVideoRequest(response.config.url)) {
      showPlanRestriction(data.data, localizedMessage(data.message, 'request.plan_restricted'))
    } else if (data.code === 4023) {
      // 团队席位是独立商品，由成员页打开购买弹窗处理。
    } else if (data.code === 1080) {
      showAgentSetup(data.data)
    } else if (data.code === 4616) {
      message.warning(t('shared.queue_full'))
    } else {
      message.error(localizedMessage(data.message, 'request.failed'))
    }
    const businessError = Object.assign(new Error(localizedMessage(data.message, 'request.failed')), {
      code: data.code,
      data: data.data,
      status: response.status,
    })
    return Promise.reject(businessError)
  },
  async (error: AxiosError) => {
    const status = error.response?.status
    const data = error.response?.data as any

    if (data?.code === 4013 && error.config && !(error.config as any)._reauthRetried) {
      try {
        const token = await requestReauth(data.data)
        const originalConfig = error.config as AxiosRequestConfig & { _reauthRetried?: boolean }
        originalConfig._reauthRetried = true
        originalConfig.headers = originalConfig.headers || {}
        originalConfig.headers['X-Reauth-Token'] = token
        return request(originalConfig)
      } catch (reauthError) {
        return Promise.reject(reauthError)
      }
    }

    if (data?.code === 4014) {
      const deniedMessage = localizedMessage(data.message, 'request.network_denied')
      message.error(deniedMessage)
      if (window.location.pathname !== '/login') clearAuthAndRedirect()
      return Promise.reject(Object.assign(new Error(deniedMessage), { code: data.code, data: data.data, status }))
    }

    // 401 或 4003：token 过期，尝试 refresh
    if (status === 401 || data?.code === 4003) {
      // 避免刷新接口本身 401 死循环
      if (error.config?.url?.includes('/auth/refresh')) {
        clearAuthAndRedirect()
        return Promise.reject(error)
      }

      const newToken = await tryRefreshToken()
      if (newToken) {
        // 重试原请求
        const originalConfig = error.config as AxiosRequestConfig
        if (originalConfig.headers) {
          originalConfig.headers.Authorization = `Bearer ${newToken}`
        }
        return request(originalConfig)
      } else {
        clearAuthAndRedirect()
        return Promise.reject(error)
      }
    }

    // 其他错误
    const errMsg = localizedMessage(data?.message || error.message, 'request.network_error')
    if (data?.code === 4012) {
      const mfaMessage = localizedMessage(data.message, 'request.mfa_required')
      message.warning(mfaMessage)
      if (window.location.pathname !== '/profile') window.location.href = '/profile?mfa=required'
      return Promise.reject(Object.assign(new Error(mfaMessage), { code: data.code, data: data.data, status }))
    } else if (data?.code === 4023) {
      // 团队席位是独立商品，由调用页面处理购买引导。
    } else if (data?.code === 1080) {
      showAgentSetup(data.data)
    } else if (data?.code === 4616) {
      message.warning(t('shared.queue_full'))
    } else if ((status === 402 || data?.code === 4021 || data?.code === 4022) && !isVideoRequest(error.config?.url)) {
      showPlanRestriction(data?.data, errMsg)
    } else {
      message.error(errMsg)
    }

    return Promise.reject(Object.assign(new Error(errMsg), {
      code: data?.code,
      data: data?.data,
      status,
    }))
  },
)

export default request
